import React, { useState, useEffect } from 'react';
import {
  Plus, RefreshCw,
  AlertCircle,
  IndianRupee,
  Settings, ClipboardList,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import {
  getAllBillingInvoices,
  getBillingServices,
  createBillingInvoice,
  updateBillingInvoice,
  recordInvoicePayment,
  cancelBillingInvoice,
  getDoctors,
} from '@/lib/api';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import InvoiceEditModal from '@/components/Patient/InvoiceEditModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import InvoiceTable from '@/components/Billing/InvoiceTable';
import { BillingListContainer } from '@/components/Billing/BillingUI';
import BillingServicesTab from '@/components/Billing/BillingServicesTab';
import { InvoiceViewModal, PaymentRecordModal } from '@/components/Billing/BillingModals';
import PrintDepositReceiptModal, { type DepositReceipt } from '@/components/Billing/PrintDepositReceiptModal';
import type { Invoice, BillingService } from '@/types';

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
const PAYMENT_MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card',
  cheque: 'cheque', netbanking: 'netbanking',
  'bank transfer': 'netbanking',
  insurance: 'other', other: 'other', advance: 'advance',
};
function toPaymentMode(method: string | undefined): PaymentMode {
  return PAYMENT_MODE_MAP[(method || '').toLowerCase()] ?? 'cash';
}

export default function BillingPage() {
  const { currentUser } = useAuth();
  const { hasFeature, can } = usePermission();
  const canManageServices = can('settings:write');
  const canCreateInvoice = can('billing:write');
  const navigate = useNavigate();
  const { formatCurrency } = useBillingUtils();

  useEffect(() => { document.title = "Billing | OneCheckUp"; }, []);

  const [showServices, setShowServices] = useState(false);
  // A bill taken with an advance: the receipt for the advance, shown once the bill is saved
  const [depositReceipt, setDepositReceipt] = useState<DepositReceipt | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [services, setServices] = useState<BillingService[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Server-side pagination: invoices holds only the current page
  // One list, newest first, grouped by day; "Show more" loads the next batch
  const PAGE = 20;
  const [itemsPerPage, setItemsPerPage] = useState(PAGE);
  // Front desk with several doctors: one doctor's bills and takings ('all' = whole clinic)
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [serverStats, setServerStats] = useState<any>(null);
  const loadSeq = React.useRef(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedService, setSelectedService] = useState<BillingService | null>(null);
  const [billingDoctors, setBillingDoctors] = useState<{id: string; name: string}[]>([]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    toast({
      title: type === 'success' ? 'Success' : 'Error',
      description: message,
      variant: type === 'success' ? 'default' : 'destructive',
    });
  };

  // Header numbers come from the server (clinic-wide), not the loaded page
  const billingStats = React.useMemo(() => {
    const byMode: Record<string, number> = serverStats?.today_received ?? {};
    return {
      pendingAmount: Number(serverStats?.pending_amount ?? 0),
      todayInvoices: Number(serverStats?.today_invoices ?? 0),
      todayReceived: Object.values(byMode).reduce((s, n) => s + Number(n || 0), 0),
      todayByMode:   byMode,
      todayRefunded: Number(serverStats?.today_refunded ?? 0),
    };
  }, [serverStats]);

  const toYMD = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Translate the quick date filter / custom range into API date params
  const getDateParams = () => {
    if (dateRange.from || dateRange.to) {
      return {
        dateFrom: dateRange.from ? toYMD(dateRange.from) : undefined,
        dateTo:   dateRange.to   ? toYMD(dateRange.to)   : undefined,
      };
    }
    const now = new Date();
    if (dateFilter === 'today') return { dateFrom: toYMD(now), dateTo: toYMD(now) };
    if (dateFilter === 'week')  return { dateFrom: toYMD(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)), dateTo: undefined };
    if (dateFilter === 'month') return { dateFrom: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: undefined };
    return { dateFrom: undefined, dateTo: undefined };
  };

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (currentUser?.clinic_id) {
      loadAllData();
    }
  }, [currentUser?.clinic_id, currentUser?.doctor_id]);

  useEffect(() => {
    setItemsPerPage(PAGE);
  }, [debouncedSearchTerm, statusFilter, dateFilter, dateRange, doctorFilter]);

  // Refetch the page when filters or pagination change (after initial load)
  useEffect(() => {
    if (!currentUser?.clinic_id || isInitialLoading) return;
    loadInvoices().catch(err => {
      console.error('Failed to load invoices:', err);
      showNotification('error', 'Failed to load invoices');
    });
  }, [debouncedSearchTerm, statusFilter, dateFilter, dateRange, doctorFilter, itemsPerPage]);

  if (!currentUser?.clinic_id) {
    return (
        <div className="p-6 bg-background min-h-screen">
          <Alert className="status-critical">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              User must have clinic_id to access billing.
            </AlertDescription>
          </Alert>
        </div>
    );
  }

  const loadAllData = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      await Promise.all([loadInvoices(), loadServices(), loadDoctors()]);
      return true;
    } catch (error) {
      console.error('Error loading billing data:', error);
      showNotification('error', 'Failed to load billing data');
      return false;
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  const loadInvoices = async () => {
    const seq = ++loadSeq.current;
    try {
      const { dateFrom, dateTo } = getDateParams();
      const data: any = await getAllBillingInvoices(currentUser.clinic_id, {
        doctorId:  currentUser.doctor_id || (doctorFilter !== 'all' ? doctorFilter : undefined),
        status:    statusFilter,
        search:    debouncedSearchTerm.trim() || undefined,
        dateFrom,
        dateTo,
        limit:  itemsPerPage,
        offset: 0,
      });

      if (seq !== loadSeq.current) return; // a newer request superseded this one

      // Backend should return { invoices, total, stats } when `offset` is sent.
      // Fall back to a bare array (older/un-restarted server) so the page never
      // silently blanks out just because the response shape didn't match.
      if (Array.isArray(data)) {
        setInvoices(data);
        setTotalCount(data.length);
      } else {
        setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
        setTotalCount(Number(data?.total ?? 0));
        if (data?.stats) setServerStats(data.stats);
      }
    } catch (error) {
      if (seq !== loadSeq.current) return;
      console.error('Failed to load invoices:', error);
      setInvoices([]);
      setTotalCount(0);
      throw error;
    }
  };

  const loadDoctors = async () => {
    try {
      const docs = await getDoctors();
      const filtered = (Array.isArray(docs) ? docs : []).filter((d: any) => d.clinic_id === currentUser?.clinic_id);
      setBillingDoctors(filtered);
    } catch {
      setBillingDoctors([]);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getBillingServices(currentUser?.clinic_id || '', currentUser?.doctor_id);

      let servicesArray = [];
      if (Array.isArray(data)) {
        servicesArray = data;
      } else if (data && Array.isArray((data as any).services)) {
        servicesArray = (data as any).services;
      } else if (data && (data as any).data && Array.isArray((data as any).data)) {
        servicesArray = (data as any).data;
      } else {
        console.warn('Unexpected services API response format:', data);
        servicesArray = [];
      }

      setServices(servicesArray);
    } catch (error) {
      console.error('Failed to load services:', error);
      setServices([]);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    const ok = await loadAllData();
    if (ok) showNotification('success', 'Data refreshed successfully');
    setIsRefreshing(false);
  };

  // Front-desk view if user has no clinical profile (doctor_id), regardless of role
  const isFrontDesk = !currentUser?.doctor_id;

  const handleNewInvoice = () => {
    setShowCreateModal(true);
  };

  const handleOpenServiceModal = () => {
    setShowServiceModal(true);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      setIsLoading(true);

      const invoicePatient = invoiceData.patient;
      if (!invoicePatient?.id) {
        showNotification('error', 'Please select a patient first');
        return;
      }

      // Single atomic call — backend handles payments + balance_forward settlement
      const created = await createBillingInvoice({
        patient_id:      invoicePatient.id,
        doctor_id:       invoiceData.doctorId || currentUser.doctor_id || '',
        clinic_id:       currentUser.clinic_id,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items,
        discount_amount: invoiceData.discount    || 0,
        discount_type:   invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        discount_reason: invoiceData.discountReason,
        tax_amount:      invoiceData.taxAmount    || 0,
        notes:           invoiceData.notes        || undefined,
        payments:                     invoiceData.payments || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount       || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode  || undefined,
      });

      // An advance taken with the bill: its receipt is one click away in the message
      const receipt: DepositReceipt | null = invoiceData.depositAmount ? {
        patient_id: invoicePatient.id, clinic_id: currentUser.clinic_id, doctor_id: invoiceData.doctorId || currentUser.doctor_id || undefined,
        amount: invoiceData.depositAmount, payment_mode: invoiceData.depositPaymentMode,
        receipt_number: created?.deposit_receipt_number,
      } : null;
      // Long enough to reach the button (success messages otherwise go in 2s)
      if (receipt) toast({ title: 'Success', description: `Invoice created · ₹${receipt.amount.toLocaleString('en-IN')} kept as advance`, duration: 10000, action: <ToastAction altText="Print advance receipt" onClick={() => setDepositReceipt(receipt)}>Print advance receipt</ToastAction> });
      else showNotification('success', 'Invoice created successfully');
      setShowCreateModal(false);
      await loadInvoices();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const errorMessage = (error as any).response?.data?.error || (error as any).message || 'Failed to create invoice';
      showNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    try {
      if (!selectedInvoice || amount <= 0) {
        showNotification('error', 'Please enter a valid payment amount');
        return;
      }

      if (amount > selectedInvoice.balance_due) {
        showNotification('error', 'Payment amount cannot exceed balance due');
        return;
      }

      setIsLoading(true);
      await recordInvoicePayment(selectedInvoice.id, {
        amount,
        payment_mode:     toPaymentMode(method),
        payment_date:     new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app:          meta?.upi_app          || undefined,
        bank_name:        meta?.bank_name         || undefined,
      });

      showNotification('success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (error) {
      console.error('Failed to record payment:', error);
      const errorMessage = (error as any).response?.data?.error || (error as any).message || 'Failed to record payment';
      showNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleUpdateInvoice = async (invoiceData: any) => {
    try {
      if (!selectedInvoice) return;

      setIsLoading(true);
      await updateBillingInvoice(selectedInvoice.id, invoiceData);

      showNotification('success', 'Invoice updated successfully');
      setShowEditModal(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      showNotification('error', error.message ?? 'Failed to update invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleCancelInvoice = async (invoice: Invoice) => {
    if (Number((invoice as any).paid_amount ?? 0) > 0) {
      showNotification('error', 'This invoice has payments. Refund them before cancelling.');
      return;
    }
    const confirmed = window.confirm(
      `Cancel invoice ${invoice.invoice_number} for ${invoice.patient_name}?\n\nThis cannot be undone. The invoice will be voided and the billed amount removed from the patient ledger.`
    );
    if (!confirmed) return;
    try {
      setIsLoading(true);
      await cancelBillingInvoice(invoice.id, 'Cancelled by user');
      showNotification('success', `Invoice ${invoice.invoice_number} cancelled`);
      await loadInvoices();
    } catch (error: any) {
      showNotification('error', error.message ?? 'Failed to cancel invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPatient = (patientId: string) => {
    navigate(isFrontDesk ? `/frontdesk/patients/${patientId}?tab=billing` : `/patients/${patientId}?tab=billing`);
  };

  const handlePrintInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPrintModal(true);
  };


  if (isInitialLoading) {
    return (
        <div className="prescription-form-container">
          <div className="border-b border-border pb-3 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-6 w-24 mb-1.5" />
                <Skeleton className="h-4 w-72" />
              </div>
              <div className="flex gap-1.5">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-28" />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <Skeleton className="h-14 rounded-lg" />
            {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        </div>
    );
  }

  return (
      <div className="prescription-form-container">

        {/* Header */}
        <div className="border-b border-border pb-3 mb-4">
          {/* Wraps on phones: title + totals, then the actions */}
          <PageHeader
            icon={IndianRupee}
            title="Billing"
            subtitle={<>
              {/* Today's takings by method — what the desk counts at closing */}
              Today {formatCurrency(billingStats.todayReceived)} received
              {billingStats.todayReceived > 0 && (
                  <span className="text-muted-foreground">
                    {' ('}{Object.entries(billingStats.todayByMode).filter(([, n]) => Number(n) > 0)
                      .map(([m, n]) => `${({ cash: 'cash', upi: 'UPI', card: 'card', netbanking: 'net banking', cheque: 'cheque', advance: 'advance', other: 'other' } as Record<string, string>)[m] ?? m} ${formatCurrency(Number(n))}`).join(' · ')}{')'}
                  </span>
              )}
              {billingStats.todayRefunded > 0 && <>{' · '}{formatCurrency(billingStats.todayRefunded)} refunded</>}
              {' · '}
              {billingStats.todayInvoices} bill{billingStats.todayInvoices === 1 ? '' : 's'}
              {' · '}
              {billingStats.pendingAmount > 0
                  ? <span className="text-warning font-medium">{formatCurrency(billingStats.pendingAmount)} outstanding</span>
                  : 'all settled'
              }
            </>}
            actions={<>
              {!currentUser.doctor_id && billingDoctors.length > 1 && (
                  <select
                      aria-label="Doctor"
                      value={doctorFilter}
                      onChange={e => setDoctorFilter(e.target.value)}
                      className="h-8 touch:h-10 max-w-[11rem] rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="all">All doctors</option>
                    {billingDoctors.map(d => <option key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</option>)}
                  </select>
              )}
              {canManageServices && (
                  <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowServices(!showServices)}
                      className={cn("gap-2", showServices && "bg-muted")}
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Services</span>
                  </Button>
              )}
              <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshData}
                  disabled={isRefreshing}
                  className="gap-2"
              >
                <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              {canCreateInvoice && (
                  <Button onClick={handleNewInvoice} size="sm" className="gap-2">
                    <ClipboardList className="w-4 h-4" />
                    New invoice
                  </Button>
              )}
            </>}
          />
        </div>

        {/* Services view */}
        {showServices ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                    onClick={() => setShowServices(false)}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Invoices
                </button>
                {canManageServices && (
                    <Button onClick={handleOpenServiceModal} size="sm" variant="outline" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Add Service
                    </Button>
                )}
              </div>
              <BillingServicesTab
                  clinicId={currentUser.clinic_id}
                  doctorId={currentUser.doctor_id}
                  canManage={canManageServices}
                  onNotification={showNotification}
                  showServiceModal={showServiceModal}
                  onCloseServiceModal={() => {
                    setShowServiceModal(false);
                    setSelectedService(null);
                  }}
                  selectedService={selectedService}
                  onServiceSaved={() => {
                    setShowServiceModal(false);
                    setSelectedService(null);
                    loadServices();
                  }}
                  onEditService={(service) => {
                    setSelectedService(service);
                    setShowServiceModal(true);
                  }}
              />
            </div>
        ) : (
            /* Invoice list */
            <BillingListContainer
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                dateFilter={dateFilter}
                onDateChange={setDateFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                variant="clinic"
                invoices={invoices}
                filteredInvoices={invoices}
                totalItems={totalCount}
                onCreateInvoice={canCreateInvoice ? handleNewInvoice : undefined}
            >
              <InvoiceTable
                  invoices={invoices}
                  selectedId={showViewModal ? selectedInvoice?.id : null}
                  onView={handleViewInvoice}
                  onEdit={canCreateInvoice ? handleEditInvoice : undefined}
                  onPrint={handlePrintInvoice}
                  onPayment={canCreateInvoice ? handlePayInvoice : undefined}
                  onCancel={can('billing:cancel') ? handleCancelInvoice : undefined}
                  onViewPatient={handleViewPatient}
                  groupByDate
              />
              {invoices.length < totalCount && (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setItemsPerPage(n => n + PAGE)}>
                    Show more · {invoices.length} of {totalCount}
                  </Button>
              )}
            </BillingListContainer>
        )}

        {/* Create Invoice Modal */}
        {showCreateModal && (
            <MedicalBillingModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                patient={null}
                services={services}
                onCreateInvoice={handleCreateInvoice}
                loading={isLoading}
                clinicId={currentUser.clinic_id}
                doctorId={currentUser.doctor_id}
                doctors={isFrontDesk ? billingDoctors : undefined}
            />
        )}

        {/* View Invoice Modal */}
        <InvoiceViewModal
            isOpen={showViewModal}
            onClose={() => setShowViewModal(false)}
            invoice={selectedInvoice}
            onPrint={handlePrintInvoice}
            onPayment={canCreateInvoice ? handlePayInvoice : undefined}
            onRefunded={loadInvoices}
            variant="clinic"
        />

        {/* Payment Modal */}
        <PrintDepositReceiptModal open={!!depositReceipt} deposit={depositReceipt} onClose={() => setDepositReceipt(null)} />

        <PaymentRecordModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedInvoice(null);
            }}
            invoice={selectedInvoice}
            onRecordPayment={(amount, method, meta) => {
              handleRecordPayment(amount, method, meta);
            }}
            loading={isLoading}
        />

        {/* Invoice Edit Modal */}
        <InvoiceEditModal
            invoice={selectedInvoice ? {
              ...selectedInvoice,
              items: selectedInvoice.items || []
            } : null}
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedInvoice(null);
            }}
            onSave={handleUpdateInvoice}
            services={services}
            loading={isLoading}
        />

        {/* Print Bill Preview Modal */}
        <PrintBillPreviewModal
            open={showPrintModal}
            invoice={selectedInvoice}
            onClose={() => {
              setShowPrintModal(false);
              setSelectedInvoice(null);
            }}
        />
      </div>
  );
}
